import { useState } from 'react';
import { StageColumn } from '../components/StageColumn';
import type { ApplicationStatus, DraftOutreachResult } from '../lib/api';
import { groupApplicationsByStage, totalTracked } from '../lib/applicationBoard';
import { isStageMovePending } from '../lib/stageMove';
import { useApplicationsBoard } from '../lib/useApplicationsBoard';
import styles from './Tracker.module.css';

/**
 * Track tab — LIVE against the real backend (the brief's (a)):
 *   GET /applications (useApplicationsBoard), rendered as six stage columns
 *   (groupApplicationsByStage, mirroring apps/web/src/pages/Tracker.tsx's own
 *   column order verbatim: Wishlist -> Applied -> Screen -> Onsite -> Offer
 *   -> Closed). Cards support BOTH a native-HTML5 drag between columns AND a
 *   per-card stage <select> fallback (StageCard.tsx), since PATCH
 *   /applications/:id allows moving to any status directly (no client-side
 *   "valid transitions" state machine — the backend has none either, see
 *   applicationTransition.ts). Silence tiers render the server's OWN
 *   silenceDays/nudgeTier/closeOffered (W1-7) verbatim, framed as an offer
 *   ("14 days quiet — mark closed?"), never an auto-move. The follow-up
 *   draft button calls POST /outreach/draft {kind:'follow_up'} and renders
 *   whatever the server actually returns — a created Gmail draft, OR a
 *   grounded refusal (422, reason+message) — verbatim, never paraphrased.
 *
 * NO backend changes (brief law (d)) — every field rendered here is read
 * straight off what GET /applications / PATCH /applications/:id / POST
 * /outreach/draft already return.
 *
 * LANE U2 (Jason: "the track the jobs is still horizontal" — a
 * side-scrolling 6-column strip, unusable on the app's actual primary
 * surface: AppShell.module.css's own doc comment confirms this page renders
 * inside a phone-width shell — 100% viewport below 1024px, capped at
 * min(480px, 90vw) above it. Tracker.module.css's `.board` now stacks stages
 * VERTICALLY by default (full-width sections, no side-scroll ANYWHERE), with
 * a `@container (min-width: 720px)` grid branch that reacts to the board's
 * own rendered width rather than the viewport — honest given how narrow
 * AppShell keeps this page even at "desktop" widths. Stage LABELS also
 * changed on this lane (display layer only — see applicationBoard.ts's
 * TRACKER_STAGE_DISPLAY_LABEL doc comment for the full key->label table and
 * the ambiguity flag); the underlying ApplicationStatus KEYS and
 * applicationTransition.ts's planTransition are untouched.
 *
 * The subtitle below no longer leads with "drag" as if it were the reliable
 * path: a grep of every drag handler in this lane (StageCard.tsx/
 * StageColumn.tsx) confirms native-HTML5 drag-and-drop only — no touch/
 * pointer-event handling, no DnD library — and the native HTML5 DnD spec
 * does not fire from a touchscreen at all. On this shell's actual primary
 * (touch) surface the stage <select> is the ONLY mechanism that works today;
 * drag remains genuinely functional for a mouse/desktop pointer in EITHER
 * layout (the drop targets are the same <section> elements regardless of
 * flex-direction, so vertical stacking cost this nothing) and was kept
 * rather than removed, per the brief's "keep it if it composes cheaply."
 */
export function Tracker() {
  const board = useApplicationsBoard();
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<ApplicationStatus | null>(null);
  const [draftingFollowUpId, setDraftingFollowUpId] = useState<string | null>(null);
  const [draftResultByApplicationId, setDraftResultByApplicationId] = useState<
    Map<string, DraftOutreachResult>
  >(new Map());

  const pending = isStageMovePending(board.moveState);

  async function handleDraftFollowUp(applicationId: string, jobId: string) {
    setDraftingFollowUpId(applicationId);
    try {
      const result = await board.draftFollowUp(jobId);
      setDraftResultByApplicationId((prev) => {
        const next = new Map(prev);
        next.set(applicationId, result);
        return next;
      });
    } finally {
      setDraftingFollowUpId(null);
    }
  }

  if (board.state.loading) {
    return (
      <div className={styles.page}>
        <Header total={0} />
        <div className={styles.status}>Loading your applications…</div>
      </div>
    );
  }

  if (board.state.error) {
    return (
      <div className={styles.page}>
        <Header total={0} />
        <div className={styles.status} role="alert">
          Couldn't load your applications: {board.state.error}
        </div>
      </div>
    );
  }

  if (board.state.applications.length === 0) {
    return (
      <div className={styles.page}>
        <Header total={0} />
        <div className={styles.emptyState}>
          <div className={styles.emptyTitle}>Nothing tracked yet</div>
          <div className={styles.emptyBody}>
            Tailor a job from Jobs, then tap Track on that job's page — it'll land right here, On my
            radar.
          </div>
        </div>
      </div>
    );
  }

  const columns = groupApplicationsByStage(board.state.applications);

  return (
    <div className={styles.page}>
      <Header total={totalTracked(board.state.applications)} />

      {board.moveError ? (
        <div className={styles.moveError} role="alert">
          {board.moveError}
        </div>
      ) : null}

      <div className={styles.board}>
        {columns.map((column) => (
          <StageColumn
            key={column.status}
            column={column}
            draggingId={draggingId}
            pendingId={board.moveState.pendingId}
            isDropTarget={dragOverStatus === column.status}
            onDragStartCard={setDraggingId}
            onDragEndCard={() => {
              setDraggingId(null);
              setDragOverStatus(null);
            }}
            onDragOverColumn={() => {
              if (!pending) setDragOverStatus(column.status);
            }}
            onDragLeaveColumn={() => setDragOverStatus((cur) => (cur === column.status ? null : cur))}
            onDropCard={(applicationId, toStatus) => {
              setDraggingId(null);
              setDragOverStatus(null);
              if (!pending) board.moveStage(applicationId, toStatus);
            }}
            onMoveStage={board.moveStage}
            draftingFollowUpId={draftingFollowUpId}
            onDraftFollowUp={handleDraftFollowUp}
            draftResultByApplicationId={draftResultByApplicationId}
            onSaveNotes={board.saveNotes}
            savingNotesId={board.notesSavingId}
          />
        ))}
      </div>
    </div>
  );
}

function Header({ total }: { total: number }) {
  return (
    <div className={styles.eyebrow}>
      <div className={styles.kicker}>Track</div>
      <div className={styles.title}>Every application, in motion</div>
      {total > 0 ? (
        <div className={styles.subtitle}>
          {total} tracked · move a card with its stage menu (or drag it on desktop).
        </div>
      ) : null}
    </div>
  );
}
